import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import React, { useEffect } from "react";
import { If, Then, Else } from "react-if";
import ErroSVG from "@/components/Dialog/alert.red.svg?react";
import WarningSVG from "@/components/Dialog/warning.svg?react";

import { useSnackbar } from "./KendoSnackbarProvider";

export { useSnackbar } from "./KendoSnackbarProvider";

// Lazy-loaded: pulls in react-markdown + sql-formatter/prismjs, which should
// only ship to users who actually trigger a markdown-formatted dialog message.
const MarkDown = React.lazy(() => import("@/components/Markdown"));

export type Color = "success" | "info" | "warning" | "error";

interface IProps {
  message: string | null | undefined;
  isMarkdownMessage?: boolean;
  onClose: () => void;
  title: string;
  autoHideDuration?: number;
  severity: Color;
  children?: React.ReactNode;
  Details?: React.ReactNode;
}

const Main: React.FC<IProps> = (props: IProps) => {
  const {
    message,
    isMarkdownMessage,
    onClose,
    title,
    severity,
    children,
    Details,
  } = props;

  const isOpen = !!message?.length;

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog className="tlx-dialog" width={650}>
      <p className={"k-dialog-content error-dialog-content"}>
        {severity === "warning" && <WarningSVG />}
        {severity === "error" && <ErroSVG />}
        <h1 className="tlx-dialog-title">{title}</h1>
      </p>

      <div className={"k-dialog-content error-messages"}>
        <If condition={!children && message}>
          <Then>
            {isMarkdownMessage ? (
              <React.Suspense fallback={message}>
                <MarkDown>{message}</MarkDown>
              </React.Suspense>
            ) : (
              message
            )}
          </Then>
          <Else>{children}</Else>
        </If>

        {Details}
      </div>
      <DialogActionsBar layout="end">
        <Button onClick={onClose} size="medium">
          Close
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

interface IErrorProps {
  message: string | null | undefined;
  isMarkdownMessage?: boolean;
  onClose: () => void;
  title: string;
  autoHideDuration?: number;
  children?: React.ReactNode;
  Details?: React.ReactNode;
}
export const Error: React.FC<IErrorProps> = props => (
  <Main
    autoHideDuration={props.autoHideDuration}
    message={props.message}
    isMarkdownMessage={props.isMarkdownMessage}
    onClose={props.onClose}
    title={props.title}
    severity="error"
  />
);
export const ErrorWithNode: React.FC<IErrorProps> = props => (
  <Main
    autoHideDuration={props.autoHideDuration}
    message={props.message}
    onClose={props.onClose}
    title={props.title}
    severity="error"
  >
    {props.children}
  </Main>
);

export const SuccessDialog: React.FC<IErrorProps> = props => (
  <Main
    autoHideDuration={props.autoHideDuration}
    message={props.message}
    onClose={props.onClose}
    title={props.title}
    Details={props.Details}
    severity="success"
  />
);
export const Success: React.FC<IErrorProps> = props => {
  const { message, onClose, Details } = props;
  const { showMessage } = useSnackbar();

  useEffect(() => {
    if (message) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      onClose?.();
      showMessage(message, "success");
    }
  }, [message, onClose, showMessage]);

  useEffect(() => {
    if (Details) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      onClose?.();
      showMessage(Details, "success");
    }
  }, [Details, onClose, showMessage]);

  return null;
};
